import React, { useEffect, useMemo, useState } from 'react';
import { createStructuredSelector } from 'reselect';
import { useDispatch, useSelector } from 'react-redux';
import { KMF_COVER_CATEGORIES, RELATIONS } from '@om/redux/actions/kaMetlhaFuneralCover/types';

import {
    OmFormInputFieldWrapper,
    OmCheckBox,
    OmSlider,
    OmLoader,
    OmSideDrawer,
    OmEfpAddCoverDrawer,
    OmButton,
    OmApplicationPopupModal,
    OmFormDropdownFieldWrapper,
    OmRadioButtonGroup,
} from '@om/component-library-react';

import { DEFAULT_VALIDATION_DELAY } from '../../../config';
import {
    getBenefitsDetails,
    getCoverType,
    getDirectFamilyMemberDetailsAdded,
    getEditQuoteDetails,
    getHighestCoverAmount,
    getLivesCovered,
    getMainMemberDetails,
    getMonthlyPremiumQuote,
    getQuoteForm,
    getValueWithThousandSeperator,
} from '@om/redux/selectors/kaMetlhaFuneralCover/quote';
import {
    addToQuote,
    addToQuoteValidated,
    editQuote,
    resetMeAndMyDirectFamily,
    setQuoteDetailsStateValue,
    setQuoteDetailsValue,
    setQuoteDrawerValue,
    syncDirectMembersCoverAmount,
    synchOtherMembersPremium,
    updatePremiumPaidupOndobEdit,
} from '@om/redux/actions/kaMetlhaFuneralCover/quote';
import { FIELD_STATUS } from '@om/redux/reducers/status';
import { getQuoteRefernceData } from '@om/redux/selectors/kaMetlhaFuneralCover/referenceData';
import { OmdsTextField } from '@mom-omds/react-design-system-components/dist/generated/components';
import './QuoteModals.scss';

const FormContent = () => {
    const {
        coverType,
        formFields,
        calculatedPremium,
        editQuoteDetails,
        highestCoverAmount,
        livesCovered,
        isdirectFamilyMemberDataAdded,
        savedMainMemberDetails,
        referenceData,
        benefits,
    } = useSelector(
        createStructuredSelector({
            coverType: getCoverType,
            formFields: getQuoteForm,
            editQuoteDetails: getEditQuoteDetails,
            calculatedPremium: getMonthlyPremiumQuote,
            highestCoverAmount: getHighestCoverAmount,
            savedMainMemberDetails: getMainMemberDetails,
            isdirectFamilyMemberDataAdded: getDirectFamilyMemberDetailsAdded,
            livesCovered: getLivesCovered,
            referenceData: getQuoteRefernceData,
            benefits: getBenefitsDetails,
        })
    );
    const { referenceHearAboutUs, referenceOnlinePlatform } = referenceData;
    const {
        dateOfBirth,
        age,
        addPartner,
        addChild,
        coverAmount,
        monthlyPremium,
        hearAboutUsPlatform,
        hearAboutUs,
        socialPlatform,
        referFriendFamily,
        referFullName,
        referCellphoneNumber,
    } = formFields;

    const [warningModelOpen, setWarningModelOpen] = useState(false);
    const [warningAgeModel, setWarningAgeModel] = useState(false);
    const actionHandler = (fieldType, value, displayValue = null) =>
        dispatch(setQuoteDetailsValue({ fieldType, value, displayValue }));
    const dispatch = useDispatch();
    const platFormLabel = {
        online: 'Online Platform',
        onRadio: 'Radio Platform',
        onTv: 'Name of Platform',
        fromBillboard: 'Name of Billboard',
        fromSomeone: 'Please enter name of referrer',
        omEmployee: 'Please enter name of referrer',
        broker: 'Please enter name of referrer',
        other: 'Please enter name of referrer',
    };

    const isChildAndSpouseValidations = useMemo(() => {
        const vaildationArray = [
            ...(hearAboutUs?.value === 'Online' ? [socialPlatform] : []),
            ...(['fromSomeone', 'omEmployee', 'broker', 'other'].includes(hearAboutUs?.displayValue)
                ? [hearAboutUsPlatform]
                : []),
            // ...(['onRadio', 'onTv', 'fromBillboard'].includes(hearAboutUs?.displayValue) ? [] : []),
            dateOfBirth,
            hearAboutUs,
        ];

        return vaildationArray.every((element) => element.status === FIELD_STATUS.VALID);
    }, [dateOfBirth, hearAboutUs, hearAboutUsPlatform, socialPlatform]);
    useEffect(() => {
        if (!coverAmount.value && coverType === KMF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY) {
            actionHandler('coverAmount', 10000);
        }
    }, [coverType]);
    useEffect(() => {
        coverType === KMF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY &&
            actionHandler(
                'selectedCoverCategory',
                addChild.value || addPartner.value
                    ? KMF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY
                    : KMF_COVER_CATEGORIES.MYSELF
            );
    }, [addPartner, addChild, coverType]);

    useEffect(() => {
        coverType === KMF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY &&
            actionHandler('monthlyPremium', calculatedPremium);
    }, [calculatedPremium]);

    const handleAddtoQuote = () => {
        !isdirectFamilyMemberDataAdded && editQuoteDetails.id && dispatch(resetMeAndMyDirectFamily());

        dispatch(
            addToQuote({
                coverType: KMF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY,
                relation: RELATIONS.MYSELF,
            })
        );
        if (isChildAndSpouseValidations && !isdirectFamilyMemberDataAdded) {
            addPartner?.value &&
                actionHandler('selectedCoverCategory', RELATIONS.SPOUSE) &&
                dispatch(
                    addToQuoteValidated({
                        coverType: KMF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY,
                        relation: RELATIONS.SPOUSE,
                    })
                );
            addChild?.value &&
                actionHandler('selectedCoverCategory', RELATIONS.CHILD) &&
                dispatch(
                    addToQuoteValidated({
                        coverType: KMF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY,
                        relation: RELATIONS.CHILD,
                    })
                );
        }
    };
    useEffect(() => {
        if (
            age?.value > 49 &&
            benefits?.benefitsDetailsOnPremium?.value === 'Yes' &&
            coverType === KMF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY
        ) {
            setWarningAgeModel(true);
        } else {
            setWarningAgeModel(false);
        }
    }, [age]);
    return (
        <>
            <OmSideDrawer
                open={
                    coverType === KMF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY && !warningModelOpen && !warningAgeModel
                }
                onOnSideDrawerClosed={() => {
                    dispatch(setQuoteDrawerValue({ fieldType: 'coverType', value: null }));
                    dispatch(
                        setQuoteDetailsStateValue({
                            fieldType: 'editQuoteDetails',
                            value: { id: null, category: null },
                        })
                    );
                    // dispatch(setSideDrawerClosed());
                }}
            >
                <div slot="side-drawer-content">
                    <OmEfpAddCoverDrawer>
                        <div className="progressive-disclosure-form-wrapper">
                            <h5 className="no-top-margin no-bottom-margin medium">Me & My Direct Family</h5>
                            <OmFormInputFieldWrapper
                                isSensitive
                                required
                                labelAlwaysShow
                                hintText={'Format : dd/mm/yyyy'}
                                name="dateOfBirth"
                                size="full"
                                align="left"
                                mask="date"
                                placeholder="DD/MM/YYYY"
                                label="Date of birth*"
                                validationDelay={DEFAULT_VALIDATION_DELAY}
                                value={dateOfBirth?.value}
                                state={dateOfBirth?.status}
                                errorMessage={dateOfBirth?.error || age?.error}
                                onPersist={(event) => actionHandler('dateOfBirth', event.detail)}
                            />
                            {
                                <>
                                    <div className="om-form-input-field-wrapper theme-default medium-margin-top">
                                        <h6 className="no-margin">
                                            <strong>Who would you like to include?</strong>
                                        </h6>
                                        <OmCheckBox
                                            id="addPartner"
                                            text=" Add my Spouse/Partner"
                                            disabled={isdirectFamilyMemberDataAdded}
                                            checked={addPartner && addPartner?.value}
                                            onOnCheckBoxChanged={(event) => actionHandler('addPartner', event.detail)}
                                        />
                                        <OmCheckBox
                                            id="addChild"
                                            text="Add my Child/Children"
                                            disabled={isdirectFamilyMemberDataAdded}
                                            checked={addChild && addChild?.value}
                                            onOnCheckBoxChanged={(event) => actionHandler('addChild', event.detail)}
                                        />
                                        {/* <br />
                                        <p className="hint-text">
                                            This cover includes children upto 21 years old, with a maximum of 6 children
                                            covered.
                                        </p> */}
                                    </div>
                                </>
                            }
                            {coverAmount?.value && dateOfBirth.status === FIELD_STATUS.VALID && !warningModelOpen && (
                                <>
                                    <h6 className="no-bottom-margin om-form-input-field-wrapper theme-default medium-margin-top">
                                        <strong>Select cover amount:</strong>
                                    </h6>
                                    <OmSlider
                                        stepString={JSON.stringify([10000, 20000, 30000, 40000, 50000, 60000])}
                                        minLabel={'P10,000'}
                                        maxLabel={'P60,000'}
                                        value={coverAmount?.value ?? 10000}
                                        valuePrefix={'P'}
                                        onSliderChange={
                                            (event) => actionHandler('coverAmount', event.detail)
                                            // setMainMemberCoverAmount(event.detail)
                                        }
                                    />

                                    <div
                                        className="cover-label-wrapper medium-margin-top"
                                        style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}
                                    >
                                        <span className="cover-label" style={{ width: '50%' }}>
                                            <h5 className="no-bottom-margin no-top-margin medium">
                                                P {getValueWithThousandSeperator(coverAmount?.value)}
                                            </h5>
                                            <h6 className="small medium no-top-margin">COVER AMOUNT</h6>
                                        </span>

                                        <span className="cover-label" style={{ width: '50%', textAlign: 'right' }}>
                                            <h5 className="no-bottom-margin no-top-margin medium">
                                                P {monthlyPremium?.value?.toFixed(2) ?? 0}
                                            </h5>
                                            <h6 className="small medium no-top-margin base-margin-bottom">
                                                MONTHLY PREMIUM
                                            </h6>

                                            {/* <h5 className="no-bottom-margin no-top-margin medium">
                                               P {+monthlyPremium.value * 12}
                                            </h5>
                                            <h6 className="small medium no-top-margin">ANNUALLY PREMIUM</h6> */}
                                        </span>
                                        {/* <span className="cover-label"  style={{width: '50%', marginLeft: 'auto'}}>
                                           
                                        </span> */}
                                    </div>
                                </>
                            )}
                            <br />
                            <div slot="modal-content" className="large-margin-top">
                                <strong className="no-bottom-margin">Cover details</strong>
                                <ul>
                                    <li>
                                        <p>
                                            You will be covered for {!dateOfBirth?.error && (dateOfBirth?.value !== null) ? 'P' +coverAmount?.value : '100%' }
                                        </p>
                                    </li>
                                    <li>
                                        <p>
                                            Your Spouse will be covered for {!dateOfBirth?.error && (dateOfBirth?.value !== null) ? 'P' +coverAmount?.value : '100%' }
                                        </p>
                                    </li>
                                    <li>
                                        <p>
                                            Up to 6 children will be covered at {!dateOfBirth?.error && (dateOfBirth?.value !== null) ? 'P' +(coverAmount?.value/2) : '50%' } each with no extra cost per child covered.
                                        </p>
                                    </li>
                                    <li>
                                        <p>
                                            Children have an age limit of 21 years next birthday or 25 years next
                                            birthday if they are a student. Special needs children do not have an age
                                            restriction.
                                        </p>
                                    </li>
                                    <li>
                                        <p>
                                        If your children do not meet any of the requirements, you will have to add them as extended family members
                                        </p>
                                    </li>
                                </ul>
                            </div>
                            <h5
                                className="medium-margin-top"
                                style={{
                                    fontWeight: '500',
                                    color: '#747474',
                                    fontSize: '22px',
                                    lineHeight: '26px',
                                }}
                            >
                                Referral
                            </h5>
                            <div slot="modal-content">
                                <span className="no-margin-top">
                                    How did you hear about the Ka-Metlha Funeral Cover?*
                                </span>
                                <OmFormDropdownFieldWrapper
                                    align="left"
                                    placeholder="Please select "
                                    placeholderAsLabel="false"
                                    formId="addInLaws"
                                    size="full"
                                    name={`hearAboutUs`}
                                    useNativeMobileDropdown
                                    required
                                    key={'hearAboutUs'}
                                    value={hearAboutUs?.value}
                                    state={hearAboutUs?.status}
                                    errorMessage={hearAboutUs?.error}
                                    options={JSON.stringify(
                                        referenceHearAboutUs &&
                                            referenceHearAboutUs?.map((item) => ({
                                                value: item.code,
                                                label: item.description,
                                            }))
                                    )}
                                    onOnSelect={(event) => {
                                        actionHandler('hearAboutUs', event.detail.value, event.detail.id);
                                    }}
                                />
                                {/* {['fromSomeone', 'omEmployee', 'broker', 'other'].includes(
                                    hearAboutUs?.displayValue
                                ) && (
                                    <>
                                        <OmFormInputFieldWrapper
                                            isSensitive
                                            required
                                            name="hearAboutUsPlatform"
                                            size="full"
                                            align="left"
                                            placeholder={platFormLabel[hearAboutUs?.displayValue]}
                                            validationDelay={DEFAULT_VALIDATION_DELAY}
                                            value={hearAboutUsPlatform?.value}
                                            state={hearAboutUsPlatform?.status}
                                            errorMessage={hearAboutUsPlatform?.error}
                                            onPersist={(event) => actionHandler('hearAboutUsPlatform', event.detail)}
                                        />
                                    </>
                                )} */}
                                <br></br>
                                {hearAboutUs?.displayValue === 'online' && (
                                    <>
                                        <OmFormDropdownFieldWrapper
                                            align="left"
                                            placeholder="Please select* "
                                            placeholderAsLabel="false"
                                            formId="socialPlatform"
                                            size="full"
                                            name={`socialPlatform`}
                                            useNativeMobileDropdown
                                            required
                                            key={'socialPlatform'}
                                            value={socialPlatform?.value}
                                            state={socialPlatform?.status}
                                            errorMessage={socialPlatform?.error}
                                            options={JSON.stringify(
                                                referenceOnlinePlatform &&
                                                    referenceOnlinePlatform?.map((item) => ({
                                                        value: item.code,
                                                        label: item.description,
                                                    }))
                                            )}
                                            onOnSelect={(event) => {
                                                actionHandler('socialPlatform', event.detail.value, event.detail.id);
                                            }}
                                        />
                                    </>
                                )}
                                <br></br>
                                {/* <p className="heading margin-top-medium">
                                    <>
                                        Is there someone among your friends or family who could also benefit from our
                                        trusted service?
                                    </>
                                </p>
                                <OmRadioButtonGroup
                                    vertical="true"
                                    groupName="referFriendsMeandMyFamily"
                                    value={referFriendFamily?.value}
                                    onOnSelection={(event) => {
                                        actionHandler('referFriendFamily', event.detail);
                                        
                                    }}
                                >
                                    <ul>
                                        <li value={'Yes'}>Yes</li>
                                        <li value={'No'}>No</li>
                                    </ul>
                                </OmRadioButtonGroup>
                                {referFriendFamily?.value === 'Yes' && (
                                    <>
                                        <OmFormInputFieldWrapper
                                            required={true}
                                            size="full"
                                            formId="allsureInsurancePersonalDetailsForm"
                                            align="left"
                                            validationDelay={DEFAULT_VALIDATION_DELAY}
                                            isSensitive
                                            id="referFullName"
                                            name="referFullName"
                                            placeholder="Full name"
                                            value={referFullName.value}
                                            state={referFullName.status}
                                            errorMessage={referFullName?.error}
                                            onPersist={({ detail }) => actionHandler('referFullName', detail)}
                                        />

                                        <OmFormInputFieldWrapper
                                            required={true}
                                            size="full"
                                            formId="allsureInsurancePersonalDetailsForm"
                                            align="left"
                                            validationDelay={DEFAULT_VALIDATION_DELAY}
                                            isSensitive
                                            id="referCellphoneNumber"
                                            placeholder="Cellphone number"
                                            name="cellphoneNumber"
                                            value={referCellphoneNumber?.value}
                                            state={referCellphoneNumber?.status}
                                            errorMessage={referCellphoneNumber?.error}
                                            onPersist={({ detail }) => actionHandler('referCellphoneNumber', detail)}
                                        />
                                    </>
                                )} */}
                            </div>
                            <div className="action-buttons">
                                {editQuoteDetails.id ? (
                                    <OmButton
                                        type="text"
                                        size="small"
                                        text="CANCEL"
                                        onClick={() => {
                                            dispatch(setQuoteDrawerValue({ fieldType: 'coverType', value: null }));
                                            dispatch(
                                                setQuoteDetailsStateValue({
                                                    fieldType: 'editQuoteDetails',
                                                    value: { id: null, category: null },
                                                })
                                            );
                                        }}
                                    />
                                ) : (
                                    <OmButton
                                        type="text"
                                        size="small"
                                        text="CLEAR ALL"
                                        onClick={() => {
                                            dispatch(
                                                setQuoteDrawerValue({
                                                    fieldType: 'coverType',
                                                    value: KMF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY,
                                                })
                                            );
                                            actionHandler('coverAmount', 10000);
                                            actionHandler('selectedCoverCategory', KMF_COVER_CATEGORIES.MYSELF);
                                        }}
                                    />
                                )}
                                <OmButton
                                    type="primary"
                                    size="small"
                                    text={'ADD TO QUOTE'}
                                    onClick={() => {
                                        if (highestCoverAmount > coverAmount.value / 2 && isChildAndSpouseValidations) {
                                            setWarningModelOpen(true);
                                            // dispatch(
                                            //     setQuoteDetailsStateValue({ fieldType: 'coverType', value: null })
                                            // );
                                        } else {
                                            handleAddtoQuote();
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </OmEfpAddCoverDrawer>
                </div>
            </OmSideDrawer>
            <OmApplicationPopupModal
                dismissible={false}
                exitable={false}
                className="funeralPopup"
                open={warningModelOpen}
                // isOverlay
                onModalClosed={() => {
                    setWarningModelOpen(false);
                }}
                name="coverAmountWarning"
            >
                <div slot="content">
                    <p>
                        Lowering the cover may impact the cover amount limit for other member, are you sure you still
                        want to proceed{' '}
                    </p>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center' }} slot="button">
                    <OmButton
                        type="primary"
                        size="small"
                        text="Yes, Please"
                        onClick={() => {
                            dispatch(synchOtherMembersPremium());
                            handleAddtoQuote();
                        }}
                    />
                    <OmButton
                        type="secondary"
                        size="small"
                        text="No, thanks"
                        onClick={() => {
                            setWarningModelOpen(false);
                            dispatch(
                                editQuote({
                                    id: Object.keys(livesCovered[KMF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY])[0],
                                    category: KMF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY,
                                    coverType: KMF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY,
                                })
                            );
                        }}
                    />
                </div>
            </OmApplicationPopupModal>
            <OmApplicationPopupModal
                dismissible={false}
                exitable={false}
                className="funeralPopup"
                open={warningAgeModel}
                // isOverlay
                onModalClosed={() => {
                    setWarningAgeModel(false);
                }}
                name="benifitWarning"
            >
                <div slot="content">
                    <p>
                        Changing the age 50 and above may impact the premium paid up benefit for main member, are you
                        sure you still want to proceed{' '}
                    </p>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center' }} slot="button">
                    <OmButton
                        type="primary"
                        size="small"
                        text="Yes, Please"
                        onClick={() => {
                            dispatch(updatePremiumPaidupOndobEdit());
                            setWarningAgeModel(false);
                            handleAddtoQuote();
                        }}
                    />
                    <OmButton
                        type="secondary"
                        size="small"
                        text="No, thanks"
                        onClick={() => {
                            setWarningAgeModel(false);
                            dispatch(
                                editQuote({
                                    id: Object.keys(livesCovered[KMF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY])[0],
                                    category: KMF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY,
                                    coverType: KMF_COVER_CATEGORIES.ME_AND_MY_DIRECT_FAMILY,
                                })
                            );
                        }}
                    />
                </div>
            </OmApplicationPopupModal>
        </>
    );
};

const MeAndMyDirectFamilyDrawer = () => {
    // if (applicationReferenceDataValid && quoteReferenceDataValid) {
    return <FormContent />;
    // }
};

export default MeAndMyDirectFamilyDrawer;
